<template>
  <o-dialog
    ref="dialog"
    :title="$t('components.purchaseOrder.actions.receiveItem.title')"
    :info="info"
    :options="[
      { text: $t('general.cancel'), type: 'cancel' },
      { text: $t('general.confirm'), type: 'confirm', variant: 'success', callback: confirm },
    ]"
    :portal="portal"
  >
    <div v-if="purchaseOrderItem" class="-mx-4 border-t- border-b p-4 bg-gray-50 dark:bg-gray-900">
      <o-field-group :label="$t('components.purchaseOrder.actions.receiveItem.quantity')" class="mb-4 block">
        <number-field v-model="purchaseOrderItem.receivedQuantity" />
      </o-field-group>

      <o-field-group :label="$t('general.notes')" class="block">
        <text-area-field v-model="purchaseOrderItem.notes" />
      </o-field-group>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import {
  NumberField,
  TextAreaField,
} from '@/components/fields'

import {
  OFieldGroup,
} from '@/components/elements'

import type { PurchaseOrder, PurchaseOrderItem } from '~/_api/models/purchaseOrder'

interface Props {
  portal?: string
  loading?: boolean
}

interface Emits {
  (e: 'update:loading', value: boolean): void
  (e: 'success'): void
  (e: 'error'): void
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<Emits>()
const { $api, $i18n, $notification } = useNuxtApp()

const dialog = ref<any>()
const purchaseOrder = ref<PurchaseOrder | null>(null)
const purchaseOrderItem = ref<PurchaseOrderItem | null>(null)

const info = computed(() => {
  return purchaseOrderItem.value?.title
})

const open = (po: PurchaseOrder, item: PurchaseOrderItem) => {
  purchaseOrder.value = po
  purchaseOrderItem.value = item?.copy()

  purchaseOrderItem.value.receivedQuantity = item.receivedQuantity || item.orderQuantity

  dialog.value.open()
}

const confirm = async () => {
  if (!purchaseOrder.value || !purchaseOrderItem.value) return

  emit('update:loading', true)

  try {
    const items = [purchaseOrderItem.value]
    const response = await $api.purchaseOrder.receiveItems(purchaseOrder.value, items)
    emit('update:loading', false)

    if (response.data) {
      $notification.toast({
        title: $i18n.t('components.purchaseOrder.actions.receiveItem.success'),
        variant: 'success',
      })

      emit('success')

      return true
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
    emit('error')
  }
}

defineExpose({
  open,
})
</script>
